import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { PriceListUerSectionData } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { useProjectData } from 'hooks/useProjectData';
import {
    gqlPriceListUerForConstraintRatio,
    uerPriceListUerSectionForConstraintRatioGql,
} from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/createOrEditRatioQueries';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { usePriceListForUerTableVariables } from './usePriceListForUerTableVariables';
import { getPriceListUerForConstraintRatioGroupedData } from './usePriceListUerForConstraintRatioGroupedData';
import type { IUseUerRatioSelectorData } from './usePriceListUerForTechPartRatioData';

export const usePriceListUerForConstraintRatioData = (props: IUseUerRatioSelectorData) => {
    const { searchValue, currentFilters } = props;
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);

    const [selectedGroup, setSelectedGroup] = useState<PriceListUerSectionData | null>(null);

    const handleChangeSelectedGroup = useCallback(({ value }: { value: PriceListUerSectionData | null }) => {
        setSelectedGroup(value);
    }, []);

    const variables = usePriceListForUerTableVariables({
        searchValue,
        currentFilters,
        contractId: projectData?.contractId,
        ratioType: RatioTypeEnum.ConstraintCoef,
        selectedGroup,
    });

    const [priceListUerForConstraintRatioData, , , request] = useRequest({
        requestId: 'priceListUerForConstraintRatio',
        query: gqlPriceListUerForConstraintRatio,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'priceListUerForConstraintRatio',
        requestFn: request,
        requestVariables: variables,
    });

    useEffect(() => {
        if (priceListUerForConstraintRatioData?.uerPriceListUerForConstraintRatio?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                getPriceListUerForConstraintRatioGroupedData(
                    priceListUerForConstraintRatioData?.uerPriceListUerForConstraintRatio?.items,
                    getRowParams?.current?.endRow,
                ),
                priceListUerForConstraintRatioData?.uerPriceListUerForConstraintRatio?.totalCount ?? 0,
            );
        }
    }, [
        getRowParams,
        priceListUerForConstraintRatioData?.uerPriceListUerForConstraintRatio?.items,
        priceListUerForConstraintRatioData?.uerPriceListUerForConstraintRatio?.totalCount,
    ]);

    const selectItemsVariables = useMemo(
        () => ({
            contractId: projectData?.contractId,
        }),
        [projectData?.contractId],
    );

    const [selectItemsData, selectItemsStatus] = useRequest({
        requestId: 'uerPriceListUerSectionForConstraintRatioGql',
        query: uerPriceListUerSectionForConstraintRatioGql,
        variables: selectItemsVariables,
    });

    return {
        selectItems: selectItemsData?.uerPriceListUerSectionForConstraintRatio?.items || [],
        selectedGroup,
        handleChangeSelectedGroup,
        selectItemsLoading: selectItemsStatus === RequestStatus.FETCHING,
        dataSource,
    };
};
